/**
 * @file CustomerProductsModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-10
 * @description What one customer already buys — the check you make before
 *              filing a formulation under them.
 *
 *              Deciding where "CF Nov 25 Blend" belongs is not a lookup, it is
 *              a recognition: this farm's products are all called "CF <month>
 *              <year> Blend", so it is theirs. That only works if you can see
 *              the list, and the list has to be of distinct products rather
 *              than of invoice lines — the same blend delivered forty times
 *              buries everything else.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import EntityLink from './EntityLink';
import { formatDate } from '../utils/dateFormatters';
import './CustomerProductsModal.css';

function CustomerProductsModal({ account, onClose }) {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/formulations/customer-blends/products/${account.id}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) throw new Error(data.error || 'Failed to load');
        setProducts(data.products || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account.id]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={account.company_name || 'Customer'}
      // Four columns, one of which is a full product name — md cut the names
      // off, which is the one thing this list exists to let you read.
      size="lg"
      className="customer-products"
    >
      <div className="cpm-body">
        <p className="cpm-hint">
          {account.delivery_line1 && <span className="cpm-where">{account.delivery_line1}</span>}
          Everything invoiced to this account, most recent first. Joint accounts
          are rolled up, so a child account shows the family's products.
        </p>

        {error && <p className="cpm-error">{error}</p>}

        {loading ? (
          <LoadingSpinner />
        ) : products.length === 0 ? (
          <p className="cpm-empty">Nothing has been invoiced to this account.</p>
        ) : (
          <div className="cpm-scroll">
          <table className="data-table cpm-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Blend line</th>
                <th className="num">Invoiced</th>
                <th>Last</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className={p.is_archived ? 'cpm-archived' : ''}>
                  <td>
                    <span className="name-with-badge">
                      <EntityLink type="inventory_item" id={p.id} variant="badge"
                                  className="account-badge-inline">
                        {p.item_code}
                      </EntityLink>
                      <EntityLink type="inventory_item" id={p.id} className="cpm-name"
                                  title={p.item_name}>
                        {p.item_name}
                      </EntityLink>
                      {p.is_archived && <span className="cpm-flag">Archived</span>}
                    </span>
                  </td>
                  <td className="cpm-line">{p.blend_line || '—'}</td>
                  <td className="num">{p.times_invoiced}</td>
                  <td>{p.last_invoiced ? formatDate(p.last_invoiced) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default CustomerProductsModal;
